import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { editUser } from 'services/registration';
import { APIErrors } from 'types/api';
import { employeesInterface } from 'views/employee-management';
import { Input } from '../input';
import cls from './editUserData.module.scss';
import { schema } from './validation';

export interface EditUserDataValue {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    userIds: string;
    employee_position_name: string;
};

const defaultValues = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '000000000',
    userIds: '',
    employee_position_name: 'stanowisko',
};

export type EditUserRequest = {
    data: EditUserDataValue
}

interface EditUserDataFormFormInterface {
    user: employeesInterface[];
    workplaceId: number;
    onCancel: (boolean) => void;
}

export const EditUserDataForm = ({
    user,
    workplaceId,
    onCancel
}: EditUserDataFormFormInterface) => {
    const { t } = useTranslation();
    
    const {
        register,
        handleSubmit,
        setError,
        setValue,
        formState: { errors },
    } = useForm<EditUserDataValue>({
        resolver: yupResolver(schema),
        defaultValues: defaultValues
    });

    const onSubmit = (formObject: EditUserDataValue) => {
        const currentUser = user[0];
        const prepareData = formObject;
        
        prepareData['workplace_id'] = workplaceId;
        prepareData['has_elearning_permission'] = currentUser.has_elearning_permission;

        editUserData.mutate({
            data: prepareData
        });
    };

    const editUserData = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        EditUserRequest
    >((payload) => editUser(payload), {
        onSuccess: (data) => {
            toast.success(t('employeeManagement.editSuccessMessage'));
            onCancel(true);
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof EditUserDataValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    useEffect(() => {
        const currentUser = user[0];
        
        if (currentUser) {
            setValue('first_name', currentUser.first_name || '');
            setValue('last_name', currentUser.last_name || '');
            setValue('phone', currentUser.phone || '000000000');
            setValue('userIds', String(currentUser.id));
            setValue('employee_position_name', currentUser.employee_position_name || 'stanowisko');
        }
    }, [user, setValue]);

    return (
        <>
            <h3 className={cls.modalHeader}>{t('employeeManagement.editUserData')}</h3>
            <form 
                className={cls.form}
                onSubmit={handleSubmit(onSubmit)} 
                noValidate
            >
                <input {...register('userIds')} type="hidden" />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="first_name"
                    name="first_name"
                    label={t('registration.firstName')}
                    error={t(errors.first_name?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="last_name"
                    name="last_name"
                    label={t('registration.lastName')}
                    error={t(errors.last_name?.message)}
                    maxLength={255}
                />
                <div className={cls.modalButtonWrapper}>
                    <Button className={cls.modalButton} styleType="border" type="button" onClick={onCancel}>
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.send2')}
                    </Button>
                </div>
            </form>
        </>
    );
};
